import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fmtNum } from "@/lib/format";

export default function Risk() {
  const [funds, setFunds] = useState([]);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/funds").then((r) => { setFunds(r.data); if (r.data[0]) setActive(r.data[0].id); });
  }, []);
  useEffect(() => {
    if (!active) return;
    api.get(`/funds/${active}/nav-history`).then((r) => setHistory(r.data));
  }, [active]);

  const framework = [
    { title: "Position Sizing", body: "Position weights are risk-budgeted per instrument, capped by fund-specific gross and net exposure limits." },
    { title: "Drawdown Governance", body: "Hard drawdown thresholds trigger de-risking. A max fund-level drawdown ceiling initiates a strategy review." },
    { title: "Leverage Discipline", body: "Leverage is set per fund and per asset class, with regime-aware adjustments and no cross-fund netting." },
    { title: "Diversification", body: "Correlation-aware allocation across FX, metals, indices, equities, and crypto within permitted mandates." },
    { title: "Stops & Hedging", body: "All open exposures carry programmatic stops. Systematic hedges are engaged when volatility crosses regime bands." },
    { title: "Independent Verification", body: "Daily reconciliation with pooled broker equity. Monthly NAV signed by independent fund administrator (planned)." },
  ];

  return (
    <div className="container-tight py-16">
      <div className="kicker mb-3">Risk Management</div>
      <h1 className="text-5xl mb-4">A quantitative risk framework, applied fund by fund.</h1>
      <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
        Every Piplight fund operates under an explicit risk budget. Below is the framework applied firm-wide, followed by the fund-specific NAV trajectory you can toggle between.
      </p>

      <div className="grid md:grid-cols-3 gap-6 mb-16">
        {framework.map((x) => (
          <div key={x.title} className="surface p-6">
            <div className="kicker mb-2">Principle</div>
            <h3 className="text-xl mb-2">{x.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{x.body}</p>
          </div>
        ))}
      </div>

      <div className="surface p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <div className="kicker mb-1">NAV Trajectory</div>
            <h3 className="text-2xl">Per-Fund NAV per Unit</h3>
          </div>
          <div className="flex gap-2">
            {funds.map((f) => (
              <button key={f.id}
                data-testid={`risk-tab-${f.id}`}
                onClick={() => setActive(f.id)}
                className={`px-4 py-2 text-xs uppercase tracking-widest border ${active === f.id ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {f.code} · {f.name.replace(" Fund", "")}
              </button>
            ))}
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={history.map((h) => ({ t: (h.timestamp || "").slice(0, 10), nav: h.nav_per_unit }))}>
              <CartesianGrid stroke="hsl(226 20% 14%)" vertical={false} />
              <XAxis dataKey="t" stroke="hsl(226 10% 55%)" fontSize={11} />
              <YAxis stroke="hsl(226 10% 55%)" fontSize={11} domain={["auto", "auto"]} />
              <Tooltip contentStyle={{ background: "hsl(226 30% 7%)", border: "1px solid hsl(226 20% 14%)", fontSize: 12 }} />
              <Line type="monotone" dataKey="nav" stroke="hsl(41 40% 54%)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-16 text-xs text-muted-foreground leading-relaxed max-w-3xl">
        <div className="kicker mb-2">Risk Disclosure</div>
        Alternative investments involve significant risk, including the potential loss of principal. Past performance is not indicative of future results. NAV/unit values shown are computed by the platform from pooled account equity and are not audited. Please read the Private Placement Memorandum before subscribing.
      </div>
    </div>
  );
}
